import { useState } from "react";
import { X, Check, Trash2, KeyRound, Copy, Share2, Send } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "../../lib/api";
import { copyToClipboard } from "../../lib/clipboard";

export function ChildEditModal({ child, canDelete, onClose, onChanged, onDeleted }) {
  const [renameVal, setRenameVal] = useState(child.name);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [accessPin, setAccessPin] = useState("");
  const [confirmRevoke, setConfirmRevoke] = useState(false);
  const [access, setAccess] = useState({ enabled: !!child.access_enabled, code: child.access_code || "" });
  const [share, setShare] = useState({ enabled: !!child.share_enabled, token: child.share_token || "" });
  const [msgs, setMsgs] = useState(child.messages || []);
  const [msgText, setMsgText] = useState("");
  const [msgBusy, setMsgBusy] = useState(false);

  const renameChild = async () => {
    if (!renameVal.trim()) return toast.error("Name can't be empty");
    try {
      await api.put(`/parent/children/${child.id}`, { name: renameVal });
      toast.success("Name saved");
      onChanged();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const deleteChild = async () => {
    if (!confirmDelete) { setConfirmDelete(true); return; }
    try {
      await api.delete(`/parent/children/${child.id}`);
      toast.success(`${child.name} removed`);
      onDeleted();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-zinc-900/50 flex items-center justify-center p-4" data-testid="child-edit-modal">
      <div className="nb-card p-6 w-full max-w-md bg-white">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-display font-bold text-xl">{child.name}</h3>
          <button onClick={onClose} data-testid="child-edit-close" className="p-1.5 rounded-lg border-2 border-zinc-900 bg-white hover:bg-zinc-100"><X className="w-4 h-4" /></button>
        </div>
        <div className="space-y-4">
          <div>
            <label className="nb-label" htmlFor="child-rename-input">Name</label>
            <input id="child-rename-input" className="nb-input mt-2" data-testid="child-rename-input"
              value={renameVal} onChange={(e) => setRenameVal(e.target.value)} maxLength={60} />
          </div>
          <button onClick={renameChild} data-testid="child-rename-save" className="nb-btn w-full bg-[#A7E4D3] py-2.5"><Check className="w-4 h-4" /> Save name</button>

          <div className="pt-4 border-t-2 border-zinc-100" data-testid="child-access-section">
            <label className="nb-label">Child access — let {child.name} edit their own timetable &amp; exams</label>
            {access.enabled ? (
              <div className="mt-2 space-y-2">
                <div className="flex items-center gap-2">
                  <input readOnly className="nb-input flex-1 text-center font-black tracking-widest" data-testid="child-access-code-out" value={access.code} />
                  <button type="button" onClick={async () => { const ok = await copyToClipboard(`${window.location.origin}/kid/auth — code ${access.code}`); ok ? toast.success("Copied — send it to them") : toast.error("Couldn't copy"); }}
                    data-testid="child-access-copy-btn" className="nb-btn bg-white px-3 py-2 text-xs shrink-0"><Copy className="w-3.5 h-3.5" /></button>
                </div>
                <p className="text-[11px] text-zinc-400 font-semibold">They log in at /kid/auth with this code, their name and the PIN you chose.</p>
                <button type="button" onClick={async () => {
                  if (!confirmRevoke) { setConfirmRevoke(true); return; }
                  try {
                    await api.delete(`/parent/children/${child.id}/access`);
                    setAccess({ enabled: false, code: "" });
                    toast.success("Access turned off");
                    onChanged(true);
                  } catch (e) { toast.error(apiError(e)); }
                }} data-testid="child-access-revoke-btn" className="nb-btn w-full bg-red-100 text-red-700 py-2 text-xs">
                  {confirmRevoke ? "Click again to turn off access" : "Turn off access"}
                </button>
              </div>
            ) : (
              <div className="mt-2 flex gap-2">
                <input className="nb-input flex-1" data-testid="child-access-pin-input" placeholder="Choose a PIN (4–6 digits)" inputMode="numeric"
                  value={accessPin} onChange={(e) => setAccessPin(e.target.value)} maxLength={12} />
                <button type="button" onClick={async () => {
                  if (accessPin.trim().length < 4) return toast.error("PIN needs at least 4 digits");
                  try {
                    const { data } = await api.post(`/parent/children/${child.id}/access`, { pin: accessPin.trim() });
                    setAccess({ enabled: true, code: data.access_code });
                    toast.success("Access on — code shown below");
                    onChanged(true);
                  } catch (e) { toast.error(apiError(e)); }
                }} data-testid="child-access-enable-btn" className="nb-btn bg-[#7DD3FC] px-4 py-2 text-sm shrink-0">
                  <KeyRound className="w-4 h-4" /> Enable
                </button>
              </div>
            )}
          </div>

          <div className="pt-4 border-t-2 border-zinc-100" data-testid="child-share-section">
            <label className="nb-label">Share link — a view-only timetable page they can save to a phone home screen</label>
            {share.enabled ? (
              <div className="mt-2 space-y-2">
                <div className="flex items-center gap-2">
                  <input readOnly className="nb-input flex-1 text-xs" data-testid="child-share-link-out" value={`${window.location.origin}/k/${share.token}`} />
                  <button type="button" onClick={async () => { const ok = await copyToClipboard(`${window.location.origin}/k/${share.token}`); ok ? toast.success("Link copied") : toast.error("Couldn't copy"); }}
                    data-testid="child-share-copy-btn" className="nb-btn bg-white px-3 py-2 text-xs shrink-0"><Copy className="w-3.5 h-3.5" /></button>
                </div>
                <button type="button" onClick={async () => {
                  try {
                    await api.delete(`/parent/children/${child.id}/share`);
                    setShare({ enabled: false, token: "" });
                    toast.success("Share link turned off");
                    onChanged(true);
                  } catch (e) { toast.error(apiError(e)); }
                }} data-testid="child-share-disable-btn" className="nb-btn w-full bg-red-100 text-red-700 py-2 text-xs">
                  Turn off share link
                </button>
              </div>
            ) : (
              <button type="button" onClick={async () => {
                try {
                  const { data } = await api.post(`/parent/children/${child.id}/share`);
                  setShare({ enabled: true, token: data.share_token });
                  toast.success("Share link created — copy it below");
                  onChanged(true);
                } catch (e) { toast.error(apiError(e)); }
              }} data-testid="child-share-enable-btn" className="nb-btn w-full bg-[#FDE68A] py-2 text-sm mt-2">
                <Share2 className="w-4 h-4" /> Create share link
              </button>
            )}
          </div>

          <div className="pt-4 border-t-2 border-zinc-100" data-testid="child-message-section">
            <label className="nb-label">Send a note to {child.name} — it appears on their kid portal and share link</label>
            {msgs.length > 0 && (
              <div className="mt-2 space-y-1.5" data-testid="child-message-list">
                {msgs.slice().reverse().map((m) => (
                  <div key={m.id} className="flex items-start justify-between gap-2 rounded-lg border-2 border-zinc-900 bg-[#FDFBF7] px-2.5 py-1.5" data-testid={`child-message-${m.id}`}>
                    <span className="text-sm font-semibold">{m.text}</span>
                    <button type="button" onClick={async () => {
                      try {
                        await api.delete(`/parent/children/${child.id}/messages/${m.id}`);
                        setMsgs((cur) => cur.filter((x) => x.id !== m.id));
                      } catch (e) { toast.error(apiError(e)); }
                    }} data-testid={`child-message-delete-${m.id}`} className="p-1 shrink-0 rounded-md border border-zinc-900 bg-red-100 hover:bg-red-200">
                      <Trash2 className="w-3 h-3 text-red-700" />
                    </button>
                  </div>
                ))}
              </div>
            )}
            <div className="flex gap-2 mt-2">
              <input className="nb-input flex-1" placeholder="e.g. Don't forget your PE kit tomorrow!" maxLength={300}
                data-testid="child-message-input" value={msgText} onChange={(e) => setMsgText(e.target.value)} />
              <button type="button" disabled={msgBusy || !msgText.trim()} data-testid="child-message-send"
                onClick={async () => {
                  setMsgBusy(true);
                  try {
                    const { data } = await api.post(`/parent/children/${child.id}/messages`, { text: msgText });
                    setMsgs((cur) => [...cur, data]);
                    setMsgText("");
                    toast.success("Note sent");
                  } catch (e) { toast.error(apiError(e)); } finally { setMsgBusy(false); }
                }}
                className="nb-btn bg-[#7DD3FC] px-3 py-2 text-sm shrink-0"><Send className="w-4 h-4" /></button>
            </div>
          </div>

          {canDelete && (
            <button onClick={deleteChild} data-testid="child-delete-btn" className="nb-btn w-full bg-red-100 text-red-700 py-2.5">
              <Trash2 className="w-4 h-4" /> {confirmDelete ? "Click again to remove — this deletes their timetable" : `Remove ${child.name}`}
            </button>
          )}

        </div>
      </div>
    </div>
  );
}